/**
 * Start a Recon room from keywords picked on the Recent table.
 *
 * The room is created empty and opened with `?ask=`, so the question arrives
 * as the chat's first message — the same path the room's start form takes.
 * The chat then decides what kind of room it is and which tables to start,
 * rather than this page guessing "market" or "story" for a pile of keywords.
 *
 * Pure, so CRA's jest can test it.
 */

/** How many keywords the question names; the rest are counted. A first
 *  message that lists two hundred terms is not a question anyone would type. */
export const MAX_NAMED = 15;

type Keyword = { keyword_value: string; avg_monthly_searches?: number | null };

/** Highest search volume first: the closest thing to "what this pile is about". */
function byVolume(keywords: Keyword[]): Keyword[] {
  return [...keywords].sort((a, b) => (b.avg_monthly_searches ?? 0) - (a.avg_monthly_searches ?? 0));
}

/** The room's name: the biggest keyword. */
export function reconName(keywords: Keyword[]): string {
  return (byVolume(keywords)[0]?.keyword_value ?? 'New room').slice(0, 200);
}

/** The room's first message. */
export function reconQuestion(keywords: Keyword[]): string {
  const terms = byVolume(keywords).map(k => k.keyword_value);
  const named = terms.slice(0, MAX_NAMED).map(t => `"${t}"`).join(', ');
  const more = terms.length - MAX_NAMED;
  return (
    `People are searching for ${named}${more > 0 ? ` and ${more} more like them` : ''}. ` +
    'What are they after, who serves them today, and what is missing?'
  );
}

/** Where the new room opens, carrying the question for its chat. */
export function reconRoomPath(roomId: number, question: string): string {
  return `/boardroom/${roomId}?ask=${encodeURIComponent(question)}`;
}
